import { fetchAccountThemes, saveAccountThemes, getAccessToken } from '@ego/api';
import { DEFAULT_THEME } from '@ego/domain';
import applyTheme, { getStoredTheme } from './applyTheme';
import { listCustomThemes, replaceCustomThemes } from './customThemes';

/**
 * Cross-device theme sync (last-write-wins full replace). localStorage stays
 * the source for instant paint; the server is the cross-device authority.
 * Anonymous visitors are untouched: pulls only run for signed-in sessions
 * and pushes swallow their (401) failures.
 */

/**
 * Set while a signed-in local change has not (yet) reached the server —
 * cleared only when a PUT lands. While set, the authoritative pull re-pushes
 * local state instead of applying the server row, so a failed push (offline,
 * rate-limited) can never be silently erased by the next pull.
 */
const DIRTY_KEY = 'ego-theme-sync-dirty';

/** Monotonic push id — only the outcome of the latest push settles the dirty flag. */
let pushSeq = 0;

/**
 * Adopts the server's theme state: replaces the stored custom themes
 * (regenerating token snapshots) and applies the stored selection. A server
 * that has never synced (null selection) is instead seeded from this device
 * when the local state is non-default. A pending never-pushed local change
 * (dirty flag) wins over the server row — it is re-pushed instead.
 */
export async function pullThemesFromServer() {
  if (localStorage.getItem(DIRTY_KEY)) {
    pushThemesToServer();
    return;
  }

  let remote;
  try {
    remote = await fetchAccountThemes();
  } catch {
    return; // offline or request failed — keep local state
  }

  if (remote.themeSelection !== null) {
    replaceCustomThemes(remote.customThemes);
    applyTheme(remote.themeSelection);
    return;
  }

  if (listCustomThemes().length > 0 || getStoredTheme() !== DEFAULT_THEME) {
    pushThemesToServer();
  }
}

/**
 * Fire-and-forget push of the current local state (selection + custom theme
 * seeds, without the device-local `tokens` snapshots). Call after every
 * theme mutation.
 */
export function pushThemesToServer() {
  const customThemes = listCustomThemes().map(({ id, name, bgHex, accentHex, textHex }) => ({
    id,
    name,
    bgHex,
    accentHex,
    textHex,
  }));
  // A dangling "custom:<id>" (theme list cleared without the selection)
  // renders as the default locally (applyTheme's fallback) — send that same
  // fallback, since the server rejects selections missing from the blob.
  let themeSelection = getStoredTheme();
  if (
    themeSelection.startsWith('custom:') &&
    !customThemes.some((t) => `custom:${t.id}` === themeSelection)
  ) {
    themeSelection = DEFAULT_THEME;
  }

  // Only signed-in changes are pending server state; an anonymous visitor's
  // theme fiddling must never mark the device dirty (a stale dirty flag
  // would clobber the account's themes on a later sign-in).
  const seq = ++pushSeq;
  if (getAccessToken()) localStorage.setItem(DIRTY_KEY, '1');

  saveAccountThemes({ themeSelection, customThemes })
    .then(() => {
      if (seq === pushSeq) localStorage.removeItem(DIRTY_KEY);
    })
    .catch((err) => {
      // 401 = anonymous push, an intentional no-op rather than a pending
      // change. Everything else keeps the dirty flag so the change is
      // re-pushed (mutation or pull) instead of silently lost.
      if (err?.status === 401 && seq === pushSeq) localStorage.removeItem(DIRTY_KEY);
    });
}
